import type { UseMutationReturnType, UseQueryReturnType } from '@tanstack/vue-query'
import type { MaybeRef } from 'vue'
import type { AlertCounts } from '@/types/alert-counts.model'
import type { Alert } from '@/types/alerts/alert.model'
import type { AssetLatestReading } from '@/types/asset-latest-readings.model'
import type { BatteryLatestReading } from '@/types/battery-latest-reading.model'
import type { CountResult } from '@/types/count-result.model'
import type { Location, LocationMarkerColor } from '@/types/map.model'
import type { MonitorLatestReadings } from '@/types/monitor-latest-readings.model'
import type { Monitor } from '@/types/monitor.model'
import type { NotificationRecipient } from '@/types/notification/recipient.model'
import type { PagedResults } from '@/types/paging/paged-results.model'
import type { PagingSortingFilteringDetails } from '@/types/paging/paging-sorting-filtering-details.model'
import type { MutationOptions, QueryOptions } from '@/types/query-options.model'
import type { ReportRecipient } from '@/types/reports/recipient.model'
import type { SiteConnections } from '@/types/site-connections.model'
import type { SiteDsmConfiguration } from '@/types/site-dsm-configuration.model'
import type { ArchiveSiteMonitorArgs, SiteMonitor } from '@/types/site-monitor.model'
import type { SiteSubscription } from '@/types/site-subscription.model'
import type { Site, SiteItems, SiteManagedFuelCost, SitesCounts, SitesCountsWithProcessing, SiteSimple } from '@/types/site.model'
import { keepPreviousData, useMutation, useQuery } from '@tanstack/vue-query'
import { unref } from 'vue'
import { AlertLevel } from '@/types/alerts/alert-level.enum'
import { SiteStatus } from '@/types/site-status.enum'
import {
  CACHE_KEY_AC_ENERGY_CONSUMERS,
  CACHE_KEY_AC_GENERATORS,
  CACHE_KEY_ALERT_COUNTS,
  CACHE_KEY_ALERT_HISTORY,
  CACHE_KEY_AMBIENT_TEMPERATURE,
  CACHE_KEY_BATTERIES,
  CACHE_KEY_COUNT,
  CACHE_KEY_DC_STORAGE,
  CACHE_KEY_GENERATORS,
  CACHE_KEY_LAST_UPDATED,
  CACHE_KEY_LATEST_READINGS,
  CACHE_KEY_LIST,
  CACHE_KEY_MANAGED_FUEL_COST,
  CACHE_KEY_MONITORS,
  CACHE_KEY_NOTIFICATIONS,
  CACHE_KEY_RECIPIENTS,
  CACHE_KEY_REPORT_RECIPIENTS,
  CACHE_KEY_SITES,
  CACHE_KEY_SITES_CONNECTIONS,
  CACHE_KEY_SITES_COUNTS,
  CACHE_KEY_SITES_DSM_CONFIGURATION,
  CACHE_KEY_SITES_FOR_DASHBOARD,
  CACHE_KEY_SITES_WITH_CRITICAL_ALERTS_COUNT,
  CACHE_KEY_SITES_WITH_HIGH_ALERTS_COUNT,
  CACHE_KEY_UNASSIGNED_MONITOR_READINGS,
} from '@/utils/constants/cache'
import { getSiteCard } from './analytics.api'
import { deleteRequest, getRequest, postPagedRequest, postRequest, putRequest } from './core/api-client'

// GET /sites
async function getSites(signal?: AbortSignal | undefined): Promise<Site[]> {
  return await getRequest<Site[]>('/sites', { signal })
}

export function getSitesQuery(useQueryOptions?: QueryOptions<Site[]>): UseQueryReturnType<Site[], Error> {
  return useQuery({
    queryKey: [CACHE_KEY_SITES],
    queryFn: async ({ signal }) => await getSites(signal),
    staleTime: 5 * 60 * 1000, // cache query for 5 minutes
    ...useQueryOptions,
  })
}

export function getSitesBreadcrumbQuery(useQueryOptions?: QueryOptions<SiteSimple[]>): UseQueryReturnType<SiteSimple[], Error> {
  return useQuery({
    queryKey: [CACHE_KEY_SITES],
    queryFn: async ({ signal }) => await getSites(signal),
    select: (sites) => {
      return (sites && sites.length > 0)
        ? sites.map((site: Site) => {
            return {
              id: site.id,
              label: site.name,
            } as SiteSimple
          })
        : undefined
    },
    staleTime: 5 * 60 * 1000, // cache query for 5 minutes
    ...useQueryOptions,
  })
}

// POST /sites/list
async function getSitesPaged(pagingSortingFilteringDetails: PagingSortingFilteringDetails, signal?: AbortSignal | undefined): Promise<PagedResults<Site>> {
  return await postPagedRequest<Site>('sites/list', pagingSortingFilteringDetails, { signal })
}

export function getSitesPagedQuery(pagingSortingFilteringDetails: PagingSortingFilteringDetails): UseQueryReturnType<PagedResults<Site>, Error> {
  return useQuery({
    queryKey: [CACHE_KEY_SITES, CACHE_KEY_LIST, pagingSortingFilteringDetails],
    queryFn: async ({ signal }) => await getSitesPaged(pagingSortingFilteringDetails, signal),
    placeholderData: keepPreviousData,
  })
}

// GET /sites/:siteId
async function getSite(siteId: string, signal?: AbortSignal | undefined): Promise<Site> {
  return await getRequest<Site>(`/sites/${siteId}`, { signal })
}

export function getSiteQuery(siteId: MaybeRef<string>, useQueryOptions?: QueryOptions<Site>): UseQueryReturnType<Site, Error> {
  return useQuery({
    queryKey: [CACHE_KEY_SITES, siteId],
    queryFn: async ({ signal }) => await getSite(unref(siteId), signal),
    staleTime: 5 * 60 * 1000, // cache query for 5 minutes
    ...useQueryOptions,
  })
}

// GET /sites/count
async function getSiteCount(signal?: AbortSignal | undefined): Promise<number> {
  return (await getRequest<CountResult>('/sites/count', { signal })).count
}

export function getSiteCountQuery(useQueryOptions?: QueryOptions<number>): UseQueryReturnType<number, Error> {
  return useQuery({
    queryKey: [CACHE_KEY_SITES, CACHE_KEY_COUNT],
    queryFn: async ({ signal }) => await getSiteCount(signal),
    staleTime: 5 * 60 * 1000, // cache query for 5 minutes
    ...useQueryOptions,
  })
}

// GET /sites/:siteId/alert-counts
async function getSiteAlertCounts(siteId: string, signal?: AbortSignal | undefined): Promise<AlertCounts> {
  return await getRequest<AlertCounts>(`/sites/${siteId}/alert-counts`, { signal })
}

export function getSiteAlertCountsQuery(siteId: MaybeRef<string>, useQueryOptions?: QueryOptions<AlertCounts>): UseQueryReturnType<AlertCounts, Error> {
  return useQuery({
    queryKey: [CACHE_KEY_SITES, siteId, CACHE_KEY_ALERT_COUNTS],
    queryFn: async ({ signal }) => await getSiteAlertCounts(unref(siteId), signal),
    ...useQueryOptions,
  })
}

// GET sites/{siteId}/generators/count
export async function getSiteGeneratorsCount(siteId: string, signal?: AbortSignal | undefined): Promise<number> {
  return (await getRequest<CountResult>(`/sites/${siteId}/generators/count`, { signal })).count ?? 0
}

export function getSiteGeneratorsCountQuery(siteId: MaybeRef<string>, useQueryOptions?: QueryOptions<number>): UseQueryReturnType<number, Error> {
  return useQuery({
    queryKey: [CACHE_KEY_SITES, siteId, CACHE_KEY_GENERATORS, CACHE_KEY_COUNT],
    queryFn: async ({ signal }) => await getSiteGeneratorsCount(unref(siteId), signal),
    staleTime: 5 * 60 * 1000, // cache query for 5 minutes
    ...useQueryOptions,
  })
}

// GET /sites/:siteId/ambient-temperature
async function getSiteAmbientTemperature(siteId: string, signal?: AbortSignal | undefined): Promise<number> {
  return await getRequest<number>(`/sites/${siteId}/ambient-temperature`, { signal })
}

export function getSiteAmbientTemperatureQuery(siteId: MaybeRef<string>, useQueryOptions?: QueryOptions<number>): UseQueryReturnType<number, Error> {
  return useQuery({
    queryKey: [CACHE_KEY_SITES, siteId, CACHE_KEY_AMBIENT_TEMPERATURE],
    queryFn: async ({ signal }) => await getSiteAmbientTemperature(unref(siteId), signal),
    ...useQueryOptions,
  })
}

// GET /sites/:siteId/last-updated
async function getSiteLastUpdated(siteId: string, signal?: AbortSignal | undefined): Promise<string> {
  return await getRequest<string>(`/sites/${siteId}/last-updated`, { signal })
}

export function getSiteLastUpdatedQuery(siteId: MaybeRef<string>, useQueryOptions?: QueryOptions<string>): UseQueryReturnType<string, Error> {
  return useQuery({
    queryKey: [CACHE_KEY_SITES, siteId, CACHE_KEY_LAST_UPDATED],
    queryFn: async ({ signal }) => await getSiteLastUpdated(unref(siteId), signal),
    ...useQueryOptions,
  })
}

// GET /sites/:siteId/monitors
async function getSiteMonitors(siteId: string, signal?: AbortSignal | undefined): Promise<SiteMonitor[]> {
  return await getRequest<SiteMonitor[]>(`/sites/${siteId}/monitors`, { signal })
}

export function getSiteMonitorsQuery(siteId: MaybeRef<string>, useQueryOptions?: QueryOptions<SiteMonitor[]>): UseQueryReturnType<SiteMonitor[], Error> {
  return useQuery({
    queryKey: [CACHE_KEY_SITES, siteId, CACHE_KEY_MONITORS],
    queryFn: async ({ signal }) => await getSiteMonitors(unref(siteId), signal),
    ...useQueryOptions,
  })
}

// GET /sites/:siteId/monitors/latest-readings
async function getSiteMonitorsLatestReadings(siteId: string, signal?: AbortSignal | undefined): Promise<MonitorLatestReadings[]> {
  return await getRequest<MonitorLatestReadings[]>(`/sites/${siteId}/monitors/latest-readings`, { signal })
}

export function getSiteMonitorsLatestReadingsQuery(siteId: MaybeRef<string>, useQueryOptions?: QueryOptions<MonitorLatestReadings[]>): UseQueryReturnType<MonitorLatestReadings[], Error> {
  return useQuery({
    queryKey: [CACHE_KEY_SITES, siteId, CACHE_KEY_MONITORS, CACHE_KEY_LATEST_READINGS],
    queryFn: async ({ signal }) => await getSiteMonitorsLatestReadings(unref(siteId), signal),
    ...useQueryOptions,
  })
}

// GET /sites/:siteId/connections
async function getSiteConnections(siteId: string, signal?: AbortSignal | undefined): Promise<SiteConnections> {
  return await getRequest<SiteConnections>(`/sites/${siteId}/connections`, { signal })
}

export function getSiteConnectionsQuery(siteId: MaybeRef<string>, useQueryOptions?: QueryOptions<SiteConnections>): UseQueryReturnType<SiteConnections, Error> {
  return useQuery({
    queryKey: [CACHE_KEY_SITES, siteId, CACHE_KEY_SITES_CONNECTIONS],
    queryFn: async ({ signal }) => await getSiteConnections(unref(siteId), signal),
    ...useQueryOptions,
  })
}

// GET /sites/:siteId/monitors/unassigned-monitor-readings
async function getSiteUnassignedMonitorsReadings(siteId: string, signal?: AbortSignal | undefined): Promise<AssetLatestReading[]> {
  return await getRequest<AssetLatestReading[]>(`/sites/${siteId}/monitors/unassigned-monitor-readings`, { signal })
}

export function getSiteUnassignedMonitorsReadingsQuery(siteId: MaybeRef<string>, useQueryOptions?: QueryOptions<AssetLatestReading[]>): UseQueryReturnType<AssetLatestReading[], Error> {
  return useQuery({
    queryKey: [CACHE_KEY_SITES, siteId, CACHE_KEY_MONITORS, CACHE_KEY_UNASSIGNED_MONITOR_READINGS],
    queryFn: async ({ signal }) => await getSiteUnassignedMonitorsReadings(unref(siteId), signal),
    ...useQueryOptions,
  })
}

// GET /sites/:siteId/batteries/unassigned-monitor-readings
async function getSiteBatteriesUnassignedMonitorReadings(siteId: string, signal?: AbortSignal | undefined): Promise<BatteryLatestReading[]> {
  return await getRequest<BatteryLatestReading[]>(`/sites/${siteId}/batteries/unassigned-monitor-readings`, { signal })
}

export function getSiteBatteriesUnassignedMonitorReadingsQuery(siteId: MaybeRef<string>, useQueryOptions?: QueryOptions<BatteryLatestReading[]>): UseQueryReturnType<BatteryLatestReading[], Error> {
  return useQuery({
    queryKey: [CACHE_KEY_SITES, siteId, CACHE_KEY_BATTERIES, CACHE_KEY_UNASSIGNED_MONITOR_READINGS],
    queryFn: async ({ signal }) => await getSiteBatteriesUnassignedMonitorReadings(unref(siteId), signal),
    ...useQueryOptions,
  })
}

// GET /sites/:siteId/notifications/recipients
async function getSiteNotificationRecipients(siteId: string, signal?: AbortSignal | undefined): Promise<NotificationRecipient[]> {
  return await getRequest<NotificationRecipient[]>(`/sites/${siteId}/notifications/recipients`, { signal })
}

export function getSiteNotificationRecipientsQuery(siteId: MaybeRef<string>, useQueryOptions?: QueryOptions<NotificationRecipient[]>): UseQueryReturnType<NotificationRecipient[], Error> {
  return useQuery({
    queryKey: [CACHE_KEY_SITES, siteId, CACHE_KEY_NOTIFICATIONS, CACHE_KEY_RECIPIENTS],
    queryFn: async ({ signal }) => await getSiteNotificationRecipients(unref(siteId), signal),
    ...useQueryOptions,
  })
}

// GET /sites/:siteId/reports/recipients
async function getSiteReportRecipients(siteId: string, signal?: AbortSignal | undefined): Promise<ReportRecipient[]> {
  return await getRequest<ReportRecipient[]>(`/sites/${siteId}/reports/recipients`, { signal })
}

export function getSiteReportRecipientsQuery(siteId: MaybeRef<string>, useQueryOptions?: QueryOptions<ReportRecipient[]>): UseQueryReturnType<ReportRecipient[], Error> {
  return useQuery({
    queryKey: [CACHE_KEY_SITES, siteId, CACHE_KEY_REPORT_RECIPIENTS],
    queryFn: async ({ signal }) => await getSiteReportRecipients(unref(siteId), signal),
    ...useQueryOptions,
  })
}

// GET /sites/:id/alerts
export async function getSiteAlerts(siteId: string, startDateTime: string, endDateTime: string, severities: string[], statuses: string[]): Promise<Alert[]> {
  return await postRequest(`/sites/${siteId}/alerts`, {
    startDateTime,
    endDateTime,
    severities,
    statuses,
    levels: [
      AlertLevel.Site,
      AlertLevel.AssetFamily,
      AlertLevel.Asset,
    ],
  })
}

export function getSiteAlertsQuery(siteId: MaybeRef<string>, startDateTime: MaybeRef<string>, endDateTime: MaybeRef<string>, severities: MaybeRef<string[]>, statuses: MaybeRef<string[]>, useQueryOptions?: QueryOptions<Alert[]>): UseQueryReturnType<Alert[], Error> {
  return useQuery({
    queryKey: [CACHE_KEY_SITES, siteId, CACHE_KEY_ALERT_HISTORY, [startDateTime, endDateTime], severities, statuses],
    queryFn: async () =>
      await getSiteAlerts(unref(siteId), unref(startDateTime), unref(endDateTime), unref(severities), unref(statuses)),
    ...useQueryOptions,
  })
}

export function getSiteLocationsForDashboardQuery(useQueryOptions?: QueryOptions<Site[]>): UseQueryReturnType<Location[], Error> {
  return useQuery({
    queryKey: [CACHE_KEY_SITES, CACHE_KEY_SITES_FOR_DASHBOARD],
    queryFn: async ({ signal }) => {
      const response = await getSites(signal)

      const locationsToExtend: Location[] = []

      const responseLength = response.length

      for (let responseIndex = 0; responseIndex < responseLength; responseIndex++) {
        const site = response[responseIndex]

        if (site) {
          const location: Location = {
            alertCounts: undefined,
            countryCode: site.countryCode,
            id: site.id,
            latitude: site.latitude,
            longitude: site.longitude,
            markerColor: undefined,
            name: site.name,
            status: SiteStatus.Offline, // default to offline
            reference: site.reference,
          }

          if (location.latitude && location.longitude) {
            let markerColor: LocationMarkerColor = 'default'

            const siteCard = await getSiteCard(location.id as string)

            let siteStatus = siteCard.status

            const siteAlertCounts = await getSiteAlertCounts(location.id as string)

            if (siteAlertCounts?.status === SiteStatus.Offline) {
              siteStatus = SiteStatus.Offline
            }

            if (siteStatus !== SiteStatus.Online) {
              markerColor = 'grey'
            }

            if (siteStatus === SiteStatus.Online) {
              location.alertCounts = siteAlertCounts.counts

              markerColor
                = siteAlertCounts.counts[2] || siteAlertCounts.counts[3] || siteAlertCounts.counts[4]
                  ? 'red'
                  : siteAlertCounts.counts[1]
                    ? 'amber'
                    : 'green'
            }

            location.markerColor = markerColor
            location.status = siteStatus

            locationsToExtend.push(location)
          }
        }
      }

      return locationsToExtend
    },
    ...useQueryOptions,
  })
}

// GET /sites/counts
async function getSitesCounts(signal?: AbortSignal | undefined): Promise<SitesCounts> {
  const response = await getRequest<SitesCountsWithProcessing>('/sites/counts', { signal })

  response.online += response.processing

  const { processing, ...result } = response

  return result
}

export function getSitesCountsQuery(useQueryOptions?: QueryOptions<SitesCounts>): UseQueryReturnType<SitesCounts, Error> {
  return useQuery({
    queryKey: [CACHE_KEY_SITES, CACHE_KEY_SITES_COUNTS],
    queryFn: async ({ signal }) => await getSitesCounts(signal),
    staleTime: 5 * 60 * 1000, // cache query for 5 minutes
    ...useQueryOptions,
  })
}

// GET /sites/with-critical-alerts/count
async function getSitesWithCriticalAlertsCount(signal?: AbortSignal | undefined): Promise<number> {
  return await getRequest<number>('/sites/with-critical-alerts/count', { signal })
}

export function getSitesWithCriticalAlertsCountQuery(useQueryOptions?: QueryOptions<number>): UseQueryReturnType<number, Error> {
  return useQuery({
    queryKey: [CACHE_KEY_SITES_WITH_CRITICAL_ALERTS_COUNT],
    queryFn: async ({ signal }) => await getSitesWithCriticalAlertsCount(signal),
    ...useQueryOptions,
  })
}

// GET /sites/with-high-alerts/count
async function getSitesWithHighAlertsCount(signal?: AbortSignal | undefined): Promise<number> {
  return await getRequest<number>('/sites/with-high-alerts/count', { signal })
}

export function getSitesWithHighAlertsCountQuery(useQueryOptions?: QueryOptions<number>): UseQueryReturnType<number, Error> {
  return useQuery({
    queryKey: [CACHE_KEY_SITES_WITH_HIGH_ALERTS_COUNT],
    queryFn: async ({ signal }) => await getSitesWithHighAlertsCount(signal),
    ...useQueryOptions,
  })
}

// GET /sites/:siteId/managed-fuel-cost
async function getSiteManagedFuelCost(siteId: string, signal?: AbortSignal | undefined): Promise<SiteManagedFuelCost> {
  return await getRequest<SiteManagedFuelCost>(`/sites/${siteId}/managed-fuel-cost`, { signal })
}

export function getSiteManagedFuelCostQuery(siteId: MaybeRef<string>, useQueryOptions?: QueryOptions<SiteManagedFuelCost>): UseQueryReturnType<SiteManagedFuelCost, Error> {
  return useQuery({
    queryKey: [CACHE_KEY_SITES, siteId, CACHE_KEY_MANAGED_FUEL_COST],
    queryFn: async ({ signal }) => await getSiteManagedFuelCost(unref(siteId), signal),
    ...useQueryOptions,
  })
}

// GET /sites/:siteId/ac-consumers
async function getSiteAcEnergyConsumers(siteId: string, signal?: AbortSignal | undefined): Promise<SiteItems[]> {
  return await getRequest<SiteItems[]>(`/sites/${siteId}/ac-consumers`, { signal })
}

export function getSiteAcEnergyConsumersQuery(siteId: MaybeRef<string>, useQueryOptions?: QueryOptions<SiteItems[]>): UseQueryReturnType<SiteItems[], Error> {
  return useQuery({
    queryKey: [CACHE_KEY_SITES, siteId, CACHE_KEY_AC_ENERGY_CONSUMERS],
    queryFn: async ({ signal }) => await getSiteAcEnergyConsumers(unref(siteId), signal),
    ...useQueryOptions,
  })
}

// GET /sites/:siteId/ac-generators
async function getSiteAcGenerators(siteId: string, signal?: AbortSignal | undefined): Promise<SiteItems[]> {
  return await getRequest<SiteItems[]>(`/sites/${siteId}/ac-generators`, { signal })
}

export function getSiteAcGeneratorsQuery(siteId: MaybeRef<string>, useQueryOptions?: QueryOptions<SiteItems[]>): UseQueryReturnType<SiteItems[], Error> {
  return useQuery({
    queryKey: [CACHE_KEY_SITES, siteId, CACHE_KEY_AC_GENERATORS],
    queryFn: async ({ signal }) => await getSiteAcGenerators(unref(siteId), signal),
    ...useQueryOptions,
  })
}

// GET /sites/:siteId/dc-storage
async function getSiteDcStorage(siteId: string, signal?: AbortSignal | undefined): Promise<SiteItems[]> {
  return await getRequest<SiteItems[]>(`/sites/${siteId}/dc-storage`, { signal })
}

export function getSiteDcStorageQuery(siteId: MaybeRef<string>, useQueryOptions?: QueryOptions<SiteItems[]>): UseQueryReturnType<SiteItems[], Error> {
  return useQuery({
    queryKey: [CACHE_KEY_SITES, siteId, CACHE_KEY_DC_STORAGE],
    queryFn: async ({ signal }) => await getSiteDcStorage(unref(siteId), signal),
    ...useQueryOptions,
  })
}

// GET /sites/:siteId/dsm-configuration
async function getSiteDsmConfiguration(siteId: string, signal?: AbortSignal | undefined): Promise<SiteDsmConfiguration> {
  return await getRequest<SiteDsmConfiguration>(`/sites/${siteId}/dsm-configuration`, { signal })
}

export function getSiteDsmConfigurationQuery(siteId: MaybeRef<string>, useQueryOptions?: QueryOptions<SiteDsmConfiguration>): UseQueryReturnType<SiteDsmConfiguration, Error> {
  return useQuery({
    queryKey: [CACHE_KEY_SITES, siteId, CACHE_KEY_SITES_DSM_CONFIGURATION],
    queryFn: async ({ signal }) => await getSiteDsmConfiguration(unref(siteId), signal),
    ...useQueryOptions,
  })
}

// POST /sites/:site
async function createSite(site: Site): Promise<Site> {
  return await postRequest<Site, Site>(`/sites`, site)
}

export function createSiteMutation(useMutationOptions?: MutationOptions<Site>): UseMutationReturnType<Site, Error, Site, Error> {
  return useMutation({
    mutationFn: async (site: Site) => await createSite(site),
    ...useMutationOptions,
  })
}

// POST /sites/:siteId/monitors
async function createSiteMonitor(siteId: string, monitor: Monitor): Promise<Monitor> {
  return await postRequest<Monitor, Monitor>(`/sites/${siteId}/monitors`, monitor)
}

export function createSiteMonitorMutation(siteId: MaybeRef<string>, useMutationOptions?: MutationOptions<Monitor>): UseMutationReturnType<Monitor, Error, Monitor, Error> {
  return useMutation({
    mutationFn: async (monitor: Monitor) => await createSiteMonitor(unref(siteId), monitor),
    ...useMutationOptions,
  })
}

// POST /sites/notifications/recipients
async function createSiteNotificationRecipient(recipient: NotificationRecipient): Promise<NotificationRecipient> {
  return await postRequest<NotificationRecipient, NotificationRecipient>(`/sites/notifications/recipients`, recipient)
}

export function createSiteNotificationRecipientMutation(useMutationOptions?: MutationOptions<NotificationRecipient>): UseMutationReturnType<NotificationRecipient, Error, NotificationRecipient, Error> {
  return useMutation({
    mutationFn: async (recipient: NotificationRecipient) => await createSiteNotificationRecipient(recipient),
    ...useMutationOptions,
  })
}

// POST /sites/reports/recipients
async function createSiteReportRecipient(recipient: ReportRecipient): Promise<ReportRecipient> {
  return await postRequest<ReportRecipient, ReportRecipient>(`/sites/reports/recipients`, recipient)
}

export function createSiteReportRecipientMutation(useMutationOptions?: MutationOptions<ReportRecipient>): UseMutationReturnType<ReportRecipient, Error, ReportRecipient, Error> {
  return useMutation({
    mutationFn: async (recipient: ReportRecipient) => await createSiteReportRecipient(recipient),
    ...useMutationOptions,
  })
}

// PUT /sites/:id
async function updateSite(site: Site): Promise<Site> {
  return await putRequest<Site, Site>(`/sites/${site.id}`, site)
}

export function updateSiteMutation(useMutationOptions?: MutationOptions<Site>): UseMutationReturnType<Site, Error, Site, Error> {
  return useMutation({
    mutationFn: async (site: Site) => await updateSite(site),
    ...useMutationOptions,
  })
}

// PUT /sites/:siteId/monitor/:id
async function updateSiteMonitor(siteId: string, monitor: Monitor): Promise<Monitor> {
  return await putRequest<Monitor, Monitor>(`/sites/${siteId}/monitors/${monitor.id}`, monitor)
}

export function updateSiteMonitorMutation(useMutationOptions?: MutationOptions<Monitor>): UseMutationReturnType<Monitor, Error, Monitor, Error> {
  return useMutation({
    mutationFn: async ({ siteId, monitor }) => await updateSiteMonitor(siteId, monitor),
    ...useMutationOptions,
  })
}

// PUT /sites/notifications/recipients/:id
async function updateSiteNotificationRecipient(recipient: NotificationRecipient): Promise<NotificationRecipient> {
  return await putRequest<NotificationRecipient, NotificationRecipient>(
    `/sites/notifications/recipients/${recipient.id}`,
    recipient,
  )
}

export function updateSiteNotificationRecipientMutation(useMutationOptions?: MutationOptions<NotificationRecipient>): UseMutationReturnType<NotificationRecipient, Error, NotificationRecipient, Error> {
  return useMutation({
    mutationFn: async (recipient: NotificationRecipient) => await updateSiteNotificationRecipient(recipient),
    ...useMutationOptions,
  })
}

// PUT /sites/reports/recipients/:id
async function updateSiteReportRecipient(recipient: ReportRecipient): Promise<ReportRecipient> {
  return await putRequest<ReportRecipient, ReportRecipient>(
    `/sites/reports/recipients/${recipient.id}`,
    recipient,
  )
}

export function updateSiteReportRecipientMutation(useMutationOptions?: MutationOptions<ReportRecipient>): UseMutationReturnType<ReportRecipient, Error, ReportRecipient, Error> {
  return useMutation({
    mutationFn: async (recipient: ReportRecipient) => await updateSiteReportRecipient(recipient),
    ...useMutationOptions,
  })
}

// PUT /sites/:id/subscription
async function updateSiteSubscription(siteId: string, siteSubscription: SiteSubscription): Promise<SiteSubscription> {
  return await putRequest<SiteSubscription, SiteSubscription>(
    `/sites/${siteId}/subscription`,
    siteSubscription,
  )
}

export function updateSiteSubscriptionMutation(useMutationOptions?: MutationOptions<SiteSubscription>): UseMutationReturnType<SiteSubscription, Error, SiteSubscription, Error> {
  return useMutation({
    mutationFn: async ({ siteId, siteSubscription }) => await updateSiteSubscription(siteId, siteSubscription),
    ...useMutationOptions,
  })
}

// PUT /sites/:siteId/dsm-configuration
async function updateSiteDsmConfiguration(siteId: string, siteDsmConfiguration: SiteDsmConfiguration): Promise<SiteDsmConfiguration> {
  return await putRequest<SiteDsmConfiguration, SiteDsmConfiguration>(
    `/sites/${siteId}/dsm-configuration`,
    siteDsmConfiguration,
  )
}

export function updateSiteDsmConfigurationMutation(useMutationOptions?: MutationOptions<SiteDsmConfiguration>): UseMutationReturnType<SiteDsmConfiguration, Error, { siteId: string, siteDsmConfiguration: SiteDsmConfiguration }, Error> {
  return useMutation({
    mutationFn: async ({ siteId, siteDsmConfiguration }: { siteId: string, siteDsmConfiguration: SiteDsmConfiguration }) =>
      await updateSiteDsmConfiguration(siteId, siteDsmConfiguration),
    ...useMutationOptions,
  })
}

// DELETE /sites/:siteId
async function archiveSite(siteId: string): Promise<boolean> {
  return await deleteRequest(`/sites/${siteId}`)
}

export function archiveSiteMutation(useMutationOptions?: MutationOptions<boolean>): UseMutationReturnType<boolean, Error, string, Error> {
  return useMutation({
    mutationFn: async (siteId: string) => await archiveSite(siteId),
    ...useMutationOptions,
  })
}

// DELETE /sites/:siteId/monitors/:monitorId
async function archiveSiteMonitor(siteId: string, monitorId: string): Promise<boolean> {
  return await deleteRequest(`/sites/${siteId}/monitors/${monitorId}`)
}

export function archiveSiteMonitorMutation(useMutationOptions?: MutationOptions<boolean>): UseMutationReturnType<boolean, Error, ArchiveSiteMonitorArgs, Error> {
  return useMutation({
    mutationFn: async ({ siteId, monitorId }) => await archiveSiteMonitor(siteId, monitorId),
    ...useMutationOptions,
  })
}

// DELETE /sites/notifications/recipients/:id
async function archiveSiteNotificationRecipient(id: string): Promise<boolean> {
  return await deleteRequest(`/sites/notifications/recipients/${id}`)
}

export function archiveSiteNotificationRecipientMutation(useMutationOptions?: MutationOptions<boolean>): UseMutationReturnType<boolean, Error, string, Error> {
  return useMutation({
    mutationFn: async (id: string) => await archiveSiteNotificationRecipient(id),
    ...useMutationOptions,
  })
}

// DELETE /sites/reports/recipients/:id
async function archiveSiteReportRecipient(id: string): Promise<boolean> {
  return await deleteRequest(`/sites/reports/recipients/${id}`)
}

export function archiveSiteReportRecipientMutation(useMutationOptions?: MutationOptions<boolean>): UseMutationReturnType<boolean, Error, string, Error> {
  return useMutation({
    mutationFn: async (id: string) => await archiveSiteReportRecipient(id),
    ...useMutationOptions,
  })
}

// used in the route guard to check if a site is active
export async function getSiteActive(siteId: string): Promise<boolean> {
  return await getRequest<boolean>(`/sites/${siteId}/active`)
}

// PUT /sites/:siteId/decommission-monitors
export async function decommissionSiteMonitors(siteId: string, endDate: string, signal?: AbortSignal): Promise<boolean> {
  const queryParams = new URLSearchParams({ endDate }).toString()
  return await putRequest<undefined, boolean>(
    `/sites/${siteId}/decommission-monitors?${queryParams}`,
    undefined,
    { signal },
  )
}

/**
 * Mutation for decommissioning site monitors
 */
export function decommissionSiteMonitorsMutation(useMutationOptions?: MutationOptions<boolean>): UseMutationReturnType<boolean, Error, { siteId: string, endDate: string }, Error> {
  return useMutation<boolean, Error, { siteId: string, endDate: string }, Error>({
    mutationFn: async ({ siteId, endDate }) => await decommissionSiteMonitors(siteId, endDate),
    ...useMutationOptions,
  })
}
